import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { ButtonOptions, ButtonStyle, CjsButton, defaultButtonOptions } from "../../button/button";
import { CjsIcon } from "../../icon/icon";
import { AcceptInputValueType, CjsInput, InputOptions } from "../input";

export interface InputWithButtonCommonOptions<InputValueType> {

    /** Options of inputWithButton */
    inputWithButton: Partial<InputWithButtonOptions>;

    /** Options of input */
    input: Partial<InputOptions<InputValueType>>;

    /** Options of button */
    button: Partial<ButtonOptions>;
}

export interface InputWithButtonOptions {

    /** 
     * Disable button, user can't click or interact with button anymore
     * @default false 
     */
    disabled?: ValueOrFunction<boolean>;

    /**
     * Force disabled state of button or input 
     * button and input disabled state will no be affected by "disabled" globale option if set 
     */
    forcedDisabled?: Partial<{
        buttonDisabled: boolean,
        inputDisabled: boolean
    }>;

    /**
     * If enable, when user will press "ENTER" on keyboard, click event is emit on button.
     * Note: if "clearButton" is enable, clear action will emit event too
     */
    enableClickOnEnter?: boolean;
}

export const defaultInputWithButtonOptions: InputWithButtonOptions = {
    disabled: false
};

export const defaultInputWithButtonButtonForcedOptions: Partial<ButtonOptions> = {
    iconMode: "only"
};

export const defaultInputWithButtonButtonOptions: ButtonOptions = {
    ...defaultButtonOptions,
    cssStyle: ButtonStyle.SECONDARY,
    outline: true,
    iconMode: "only",
    icon: {
        name: "",
        fixedWidth: true
    }
};

/**
 * Class representing a component with an input text and a button
 * Can set a callback when click on the button and change text value
 */
@CustomElement({
    name: "cjs-input-with-button",
    style: require("./inputWithButton.scss"),
    template: require("./inputWithButton.html"),
    elementCssClasses: [W_100]
})
export class CjsInputWithButton<InputValueType extends AcceptInputValueType> extends WebComponent {

    /** components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsButton, CjsInput] })
    public readonly components!: {
        inputWithButtonContainer: HTMLDivElement,
        inputText: CjsInput<InputValueType>,
        inputField: HTMLDivElement,
        button: CjsButton
    };

    @TypedWebComponentOptions(defaultInputWithButtonOptions)
    public readonly options!: WebComponentOptions<InputWithButtonOptions>;

    /** get the value of disabled property of options  */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.options.disabled));
    }

    /** Shortcut for input options */
    public get inputOptions(): WebComponentOptions<InputOptions<InputValueType>> {
        return this.input.options;
    }

    /** Shortcut for button options */
    public get buttonOptions(): WebComponentOptions<ButtonOptions> {
        return this.button.options;
    }

    /** Get input component */
    public get input(): CjsInput<InputValueType> {
        return this.components.inputText;
    }

    /** Get button component */
    public get button(): CjsButton {
        return this.components.button;
    }

    constructor(options?: Partial<InputWithButtonCommonOptions<InputValueType>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options?.inputWithButton);
        this.buttonOptions.setWithoutRefresh(options?.button);
        this.inputOptions.setAndRefreshIfChanged(options?.input);
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and components is ready
     */
    public override init(): void {
        this._initListeners();
    }

    /**
     * Refresh display
     */
    public override refresh(): void {

        // Force default options config of Button for inputWithButton, avoid other button parameters non desired behaviors
        this.buttonOptions.setWithoutRefresh(defaultInputWithButtonButtonForcedOptions);

        // Force fixedWith for button icon
        if (this.buttonOptions.icon) {
            this.buttonOptions.setWithoutRefresh({
                icon: this.buttonOptions.icon instanceof CjsIcon ? this.buttonOptions.icon : {
                    ...this.buttonOptions.icon,
                    fixedWidth: true
                }
            });
        }

        // Set disabled state
        this.inputOptions.setWithoutRefresh({ disabled: this.options.forcedDisabled?.inputDisabled != null ? this.options.forcedDisabled.inputDisabled : this.disabled });
        this.buttonOptions.setWithoutRefresh({ disabled: this.options.forcedDisabled?.buttonDisabled != null ? this.options.forcedDisabled.buttonDisabled : this.disabled });
        // Refresh components
        this.button.refresh();
        this.input.refresh();
    }

    /**
     * Set value and inputText value 
     * @param value string value
     */
    public setValue(value: InputValueType): void {
        this.inputOptions.setWithoutRefresh({ value });
        this.input.value = value;
    }

    /**
     * Set disabled on inputText and button
     */
    public setDisabled(disabled: boolean): void {
        // Refresh disabled state
        this.options.setWithoutRefresh({ disabled });

        // Input
        this.inputOptions.setAndRefreshIfChanged({ disabled: this.disabled });

        // Button
        this.buttonOptions.setAndRefreshIfChanged({ disabled: this.disabled });
    }


    /**
     * Redirect focus on input
     */
    public override focus(options?: FocusOptions): void {
        this.input.focus(options);
    }

    /**
     * Calls select on the input if available
     */
    public select(): void {
        this.input.select();
    }

    /**
     * Dispatch event on input
     */
    public dispatchEventOnInput(event: Event): boolean {
        return this.input.dispatchEvent(event);
    }

    /** Init listeners of input with button */
    private _initListeners(): void {

        // Keyboard keydown
        this.addEventListener("keydown", this._onKeyboardKeydown);

        // Clear btn click
        this.input.clearEvent.on(this._onInputClear);
    }

    /** Callback triggered when keyboard key is pressed */
    private _onKeyboardKeydown: EventListenerObject = {
        handleEvent: (e: KeyboardEvent): void => {

            // Check if key is ENTER and 'enableClickOnEnter' is enable
            if (e.key !== KeyboardInput.ENTER || !this.options.enableClickOnEnter) { return; }

            // Emit click event on button (not native html "click" event)
            this.button.clickEvent.emit();
        }
    };

    /** Callback triggered when clear button on input is clicked */
    private _onInputClear: EventListenerObject = {
        handleEvent: (): void => {

            // Check if 'enableClickOnEnter' is enable
            if (!this.options.enableClickOnEnter) { return; }

            // Emit click event on button (not native html "click" event)
            this.button.clickEvent.emit();
        }
    };
}